import { useState, useEffect, useRef, MutableRefObject } from 'react';
import MessageActions from '../../message-actions/MessageActions';
import { convertMarkdownToHTML } from '../../../utils/convertMarkdownToHTML';
import { useGetAgentKbUrlMapQuery } from '../../../store/chatbox/chatboxApi';

const THIRTY_MIN = 30 * 60 * 1000; // 1,800,000 ms

const TypingMessage = ({
  message,
  chatId,
  typingSpeed = 3000,
  isAuth,
  audioRef,
  hotelSub, // optional: pass active hotel's sub
  topicId
}: {
  message: string;
  chatId?: string;
  typingSpeed: number;
  isAuth?: boolean;
  audioRef?: MutableRefObject<
    HTMLAudioElement | { pause: () => void; paused: boolean; currentTime: number } | null
  >;
  hotelSub?: string;
  topicId: string
}) => {
  const [arrayHtml, setArrayHtml] = useState<string[][]>();
  const [showActions, setShowActions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use provided sub or fallback (replace fallback with your own source of truth)
  const SUB = hotelSub ?? '92d51424-c041-70f4-f6d2-986b0c3cb9ce';

  // Fetch { name: fileUrl } map; refetch every 30 minutes
  const { data: nameUrlMap = {} } = useGetAgentKbUrlMapQuery(SUB, {
    pollingInterval: THIRTY_MIN,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: true,
  });

  // ----- helpers to keep links fresh -----
  function rewriteAnchorsInHtmlSnippet(html: string, nameToUrl: Record<string, string>): string {
    const div = document.createElement('div');
    div.innerHTML = html;

    const anchors = div.querySelectorAll('a');
    anchors.forEach((a) => {
      const disp = (a.textContent || '').trim();
      const mapped = nameToUrl[disp];
      if (mapped) {
        a.setAttribute('href', mapped);
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    });

    return div.innerHTML;
  }

  function rewriteArrayHtmlWithMap(
    original: string[][] | undefined,
    nameToUrl: Record<string, string>
  ): string[][] | undefined {
    if (!original || !original.length) return original;
    return original.map((group) => group.map((html) => rewriteAnchorsInHtmlSnippet(html, nameToUrl)));
  }
  // ---------------------------------------

  function isMobile() {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  function splitTextPreservingHtml(
    text: string,
    maxLineLength = isAuth ? (isMobile() ? 42 : 52) : 80
  ): string[] {
    const cleanedText = text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const lines: string[] = [];
    let currentLine = '';
    let inTag = false;
    let tagBuffer = '';
    let visible = 0; // counts only visible chars (outside tags)
    let anchorDepth = 0; // >0 means inside <a>…</a>
    let lastSafeHtmlLen: number | null = null; // string length at last safe split
    let lastSafeVisible: number | null = null; // visible count at last safe split

    const pushLine = (sliceLen?: number, sliceVisible?: number) => {
      if (sliceLen != null) {
        const head = currentLine.slice(0, sliceLen);
        if (head.trim()) lines.push(head.trim());
        currentLine = currentLine.slice(sliceLen);
        visible = Math.max(0, visible - (sliceVisible ?? 0));
      } else {
        const s = currentLine.trim();
        if (s) lines.push(s);
        currentLine = '';
        visible = 0;
      }
      lastSafeHtmlLen = null;
      lastSafeVisible = null;
    };

    for (let i = 0; i < cleanedText.length; i++) {
      const ch = cleanedText[i];

      // tag start
      if (ch === '<') {
        inTag = true;
        tagBuffer = '<';
        continue;
      }

      // tag end
      if (ch === '>' && inTag) {
        tagBuffer += '>';
        inTag = false;

        const lower = tagBuffer.toLowerCase();

        // <li> or <p> starts a new array element
        if (/^<(li|p)[\s>]/.test(lower)) {
          if (currentLine.trim()) pushLine(); // flush whatever was before
          currentLine += tagBuffer; // start chunk with the opening tag
          tagBuffer = '';
          continue;
        }

        // </li> closes this element; include it and flush
        if (lower.startsWith('</li')) {
          currentLine += tagBuffer;
          pushLine(); // flush li chunk
          tagBuffer = '';
          continue;
        }

        // track anchors so we don't split inside links
        if (/^<a[\s>]/.test(lower)) {
          anchorDepth++;
        } else if (lower.startsWith('</a')) {
          anchorDepth = Math.max(0, anchorDepth - 1);
        }

        currentLine += tagBuffer; // append any other tag
        tagBuffer = '';
        continue;
      }

      // inside tag
      if (inTag) {
        tagBuffer += ch;
        continue;
      }

      // newline boundary
      if (ch === '\n') {
        if (currentLine.trim()) pushLine();
        continue;
      }

      // visible text
      currentLine += ch;
      visible += 1;

      // update "last safe split" marker on spaces OUTSIDE anchors
      if (ch === ' ' && anchorDepth === 0) {
        lastSafeHtmlLen = currentLine.length; // HTML length to this space
        lastSafeVisible = visible; // visible count to this space
      }

      // if we've exceeded the limit, split at the LAST safe space BEFORE the limit
      if (visible > maxLineLength && lastSafeHtmlLen != null) {
        pushLine(lastSafeHtmlLen, lastSafeVisible ?? 0);
      }
    }

    // flush tail
    if (currentLine.trim()) pushLine();

    return lines;
  }

  useEffect(() => {
    const chunks = convertMarkdownToHTML(message);
    let fullOutputArray: string[][] = [];
    let charIndex = 0;
    let timeoutId: number | undefined;

    setShowActions(false);

    const typeNext = () => {
      const lines = chunks
        .split(/\n+/)
        .filter((chunk) => chunk.trim() !== '')
        .map((chunk) => splitTextPreservingHtml(chunk));

      if (charIndex < lines.length) {
        fullOutputArray = [...fullOutputArray, lines[charIndex]];
        // apply freshest fileUrl mapping as we type
        const nextArray = rewriteArrayHtmlWithMap(fullOutputArray, nameUrlMap);
        setArrayHtml(nextArray);

        charIndex++;
        timeoutId = window.setTimeout(typeNext, typingSpeed);
      } else {
        setShowActions(true);
      }
    };

    typeNext();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [message, typingSpeed, nameUrlMap]);

  // If the map updates later (auto-refetch at 30m), reapply to already-rendered HTML
  useEffect(() => {
    if (!arrayHtml || !Object.keys(nameUrlMap).length) return;
    setArrayHtml((prev) => rewriteArrayHtmlWithMap(prev, nameUrlMap));
  }, [nameUrlMap]); // ensures existing links get refreshed tokens too

  return (
    <div className="bot-message dark:text-white font-normal text-sm content-item" ref={containerRef}>
      {arrayHtml?.map((arr) =>
        arr.map((item, index) => (
          <p
            style={{ animationDelay: `${index * 200}ms` }}
            key={item + index}
            className="fade-word"
            dangerouslySetInnerHTML={{ __html: item }}
          />
        ))
      )}
      {chatId && showActions && (
        <MessageActions
          chatId={chatId}
          message={message}
          topicId={topicId}
          audioRef={audioRef}
          showOptions
        />
      )}
    </div>
  );
};

export default TypingMessage;
